import { useState } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/da2a2mlv7gurs51efa2g';

type Status = { type: 'idle' | 'success' | 'error'; message: string };

export default function Contact() {
  const [status, setStatus] = useState<Status>({ type: 'idle', message: '' });
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus({ type: 'success', message: '感谢你的反馈，我们会尽快查看处理。' });
      return;
    }
    data.delete('website_alt');

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === 'email') {
        payload.append('email', String(value));
      } else {
        payload.append(key, String(value));
      }
    });

    setSubmitting(true);
    setStatus({ type: 'idle', message: '' });
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      const isSpam = /spam/i.test(String(serverMsg));
      if (!response.ok || parsed?.code !== 'OK' || isSpam) {
        setStatus({ type: 'error', message: serverMsg || '提交失败，请稍后再试。' });
        return;
      }
      setStatus({ type: 'success', message: '提交成功！感谢你的宝贵反馈。' });
      form.reset();
    } catch {
      setStatus({ type: 'error', message: '网络异常，提交失败，请稍后再试。' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="contact" className="w-full py-16 md:py-24 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
        <div>
          <span className="inline-flex items-center gap-2 text-primary-600 text-sm font-medium">
            <i className="ri-chat-3-line" aria-hidden="true"></i>
            意见反馈
          </span>
          <h2 className="mt-4 text-2xl md:text-4xl text-foreground-950">遇到问题？欢迎告诉我们</h2>
          <p className="mt-5 text-foreground-700 text-sm md:text-base leading-relaxed">
            如果你在访问歪歪官方网站入口、登录或使用过程中遇到任何问题，或有内容建议，欢迎填写右侧表单反馈。我们会认真阅读每一条意见，持续优化这份入口指南。
          </p>

          <div className="mt-8 flex flex-col gap-4">
            <div className="flex items-center gap-4 rounded-lg bg-background-100 border border-background-200 p-5">
              <span className="inline-flex w-11 h-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                <i className="ri-mail-line text-xl" aria-hidden="true"></i>
              </span>
              <div>
                <p className="text-sm text-foreground-600">联系邮箱</p>
                <p className="text-sm md:text-base text-foreground-950 mt-0.5">service@yy-nav.example.com</p>
              </div>
            </div>
            <div className="flex items-center gap-4 rounded-lg bg-background-100 border border-background-200 p-5">
              <span className="inline-flex w-11 h-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                <i className="ri-time-line text-xl" aria-hidden="true"></i>
              </span>
              <div>
                <p className="text-sm text-foreground-600">反馈处理时间</p>
                <p className="text-sm md:text-base text-foreground-950 mt-0.5">工作日 9:00 - 18:00，24小时内回复</p>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          data-readdy-form="歪歪官方网站意见反馈表单"
          className="rounded-xl border border-background-200 bg-background-100 p-6 md:p-8 flex flex-col gap-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-sm text-foreground-800">您的称呼</span>
              <input
                type="text"
                name="name"
                required
                maxLength={30}
                placeholder="请输入称呼"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 outline-none"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm text-foreground-800">联系邮箱</span>
              <input
                type="email"
                name="email"
                required
                placeholder="请输入邮箱"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 outline-none"
              />
            </label>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm text-foreground-800">反馈类型</span>
            <select
              name="category"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 outline-none"
            >
              <option value="入口问题">入口问题</option>
              <option value="登录问题">登录问题</option>
              <option value="内容建议">内容建议</option>
              <option value="其他">其他</option>
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm text-foreground-800">反馈内容</span>
            <textarea
              name="message"
              required
              maxLength={500}
              rows={5}
              placeholder="请详细描述你遇到的问题或建议（500字以内）"
              className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 outline-none"
            ></textarea>
          </label>

          <input
            type="text"
            name="website_alt"
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            readOnly
            className="hp-field"
          />

          {status.type === 'success' && (
            <p className="rounded-md bg-accent-100 text-accent-900 px-4 py-3 text-sm">{status.message}</p>
          )}
          {status.type === 'error' && (
            <p className="rounded-md bg-primary-100 text-primary-800 px-4 py-3 text-sm">{status.message}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60"
          >
            <i className="ri-send-plane-line" aria-hidden="true"></i>
            {submitting ? '提交中...' : '提交反馈'}
          </button>
        </form>
      </div>
    </section>
  );
}